import { Box } from '@mui/joy';
import { CellValueChangedEvent } from 'ag-grid-community';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProblemNumber } from 'bom/elements';
import { SolveProblemsStep } from 'bom/enums';
import { useBomImportActions } from 'bom/hooks';
import { useBomPartsImportContext } from 'bom/states';
import { IImportManufacturer } from 'bom/types';
import { Button, Notification } from 'core/components';
import { NotificationCard, Title } from 'core/elements';
import { NotificationCardType, NotificationPosition } from 'core/enums';
import { AgGridClientTableProvider, useInnerStepsContext } from 'core/states';
import { isEmptyObject } from 'core/utils';

import { getColumnDefs } from './columns';
import Table from './Table';
import { mapCreateImportManufacturersData } from './utils';

interface IProps {
  records: { [key: string]: number }[];
  isLoading: boolean;
  isValid: boolean;
}

const UnknownManufacturers = ({ records = [], isLoading = false, isValid = false }: IProps) => {
  const [mappedRecords, setMappedRecords] = useState<IImportManufacturer[]>([]);

  const { t } = useTranslation();

  const { goNext } = useInnerStepsContext();
  const { partImportId, setProblems, isAlternateVersionEnabled } = useBomPartsImportContext();

  const { createImportPartManufacturers } = useBomImportActions();

  useEffect(() => {
    if (isLoading || isEmptyObject(records)) {
      return;
    }

    setMappedRecords(
      Object.keys(records).map((record, index) => ({
        id: index,
        name: record,
        assignTo: null,
        omit: true,
      }))
    );
  }, [isLoading, records]);

  useEffect(() => {
    if (!isValid) {
      return;
    }

    setTimeout(() => {
      goNext();
    }, 1000);
  }, [isValid, goNext]);

  const defaultColDef = useMemo(
    () => ({
      wrapHeaderText: true,
      autoHeaderHeight: true,
      filter: false,
      floatingFilter: false,
      sortable: false,
      suppressMovable: true,
      enableRowGroup: false,
      maxWidth: 400,
    }),
    []
  );

  const columnDefs = useMemo(
    () => getColumnDefs(isAlternateVersionEnabled),
    [isAlternateVersionEnabled]
  );

  const onCellValueChanged = (_, { colDef: { field }, data, newValue }: CellValueChangedEvent) => {
    if ('assignTo' === field) {
      data.omit = !newValue;
    }

    if ('omit' === field && true === newValue) {
      data.assignTo = null;
    }

    data[field] = newValue;

    return { updatedRow: data, options: { redrawRows: false } };
  };

  const onSubmit = async () => {
    const { success } = await createImportPartManufacturers(
      mapCreateImportManufacturersData(mappedRecords, partImportId)
    );

    if (!success) {
      return;
    }

    setProblems((prev) => ({
      ...prev,
      [SolveProblemsStep.UNKNOWN_MANUFACTURERS]: {
        solved: mappedRecords?.length,
        unsolved: 0,
      },
    }));

    goNext();
  };

  const content =
    isLoading || isValid ? (
      <>
        <Title text={t('titleIdentifyManufacturers')} />

        <NotificationCard
          title={isLoading ? t('titleStatus') : t('titleSuccess')}
          message={
            isLoading ? t('messageManufacturersValidating') : t('messageManufacturersValidated')
          }
          type={isLoading ? NotificationCardType.LOADING : NotificationCardType.SUCCESS}
        />
      </>
    ) : (
      <>
        <Title
          text={t('titleIdentifyManufacturers')}
          subtitle={
            <>
              <ProblemNumber number={mappedRecords?.length} />
              <ProblemNumber number={0} solved />
            </>
          }
        />

        <Notification position={NotificationPosition.DRAWER} />

        <AgGridClientTableProvider
          tableId="unknownManufacturers"
          entityType="Manufacturer"
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          data={mappedRecords}
          isDataLoading={isLoading}
          onCellValueChanged={onCellValueChanged}
          loadingPercentage={isLoading ? 0 : 100}
          dataCurrentPage={isLoading ? 0 : 1}
          sideBar={{}}
          autoSizeStrategy={{
            type: 'fitCellContents',
          }}
          headerCheckbox={!isLoading}
        >
          <Table />
        </AgGridClientTableProvider>
      </>
    );

  return (
    <>
      {content}

      <Box>
        <Button
          text={
            isValid ? t('buttonLabelGoToTheNextStep') : t('buttonLabelSubmitUnknownManufacturers')
          }
          onClick={onSubmit}
          disabled={isLoading}
        />
      </Box>
    </>
  );
};

export default UnknownManufacturers;
